import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  ExclamationTriangleIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import PanelNotifications, {
  usePanelNotifications,
} from '../../../components/compliance/PanelNotifications';
import tprmService from '../../../services/compliance/tprm.service';
import { RiskAcceptance, RiskAcceptanceCreate } from '../../../types/compliance';
import ModalHeader from '../../../components/ui/ModalHeader';
import { useModalDrag } from '../../../hooks/useModalDrag';
import { useUserNames } from '../../../hooks/useUserNames';

interface Props {
  vendorId: string;
}

type FormState = {
  reason: string;
  conditions: string;
  expiry_date: string;
};

const emptyForm: FormState = { reason: '', conditions: '', expiry_date: '' };

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso + (iso.length === 10 ? 'T00:00:00' : '')).toLocaleDateString();
  } catch {
    return iso;
  }
}

function formatDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function excerpt(text: string, n = 100): string {
  if (!text) return '';
  if (text.length <= n) return text;
  return text.slice(0, n) + '…';
}

export default function RiskAcceptancesPanel({ vendorId }: Props) {
  const { nameFor } = useUserNames();
  // Destructure so `notifyX` refs are stable across renders (they're
  // useCallback([])). Putting the aggregate `toasts` object in a
  // useCallback dep triggers a re-memo on every error/success state
  // change — which for the `load` callback + its useEffect produces an
  // infinite fetch loop when the endpoint errors.
  const {
    error: toastError,
    success: toastSuccess,
    clear: clearToasts,
    notifyError,
    notifySuccess,
    notifyFailure,
  } = usePanelNotifications();
  const [rows, setRows] = useState<RiskAcceptance[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(modalOpen);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await tprmService.listRiskAcceptances(vendorId);
      setRows(data);
    } catch (error) {
      notifyFailure('Failed to load risk acceptances', error);
    } finally {
      setLoading(false);
    }
  }, [vendorId, notifyFailure]);

  useEffect(() => {
    load();
  }, [load]);

  // ESC-to-close
  useEffect(() => {
    if (!modalOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setModalOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [modalOpen]);

  const active = useMemo(() => rows.find((r) => r.is_active) || null, [rows]);
  const inactive = useMemo(() => rows.filter((r) => !r.is_active), [rows]);

  const openCreate = () => {
    setForm(emptyForm);
    setModalOpen(true);
  };

  const handleDeactivate = async (ra: RiskAcceptance) => {
    const ok = window.confirm(
      'Deactivate this risk acceptance? The vendor risk will no longer be considered accepted.',
    );
    if (!ok) return;
    try {
      await tprmService.deactivateRiskAcceptance(vendorId, ra.id);
      notifySuccess({
        title: 'Deactivated',
        message: 'Risk acceptance deactivated.',
      });
      load();
    } catch (error) {
      notifyFailure('Failed to Deactivate', error);
    }
  };

  const handleSave = async () => {
    const trimmedReason = form.reason.trim();
    if (!trimmedReason) {
      notifyError({ title: 'Reason required', message: 'Please provide a reason.' });
      return;
    }
    setSaving(true);
    try {
      const body: RiskAcceptanceCreate = {
        reason: trimmedReason,
        conditions: form.conditions,
        expiry_date: form.expiry_date || null,
      };
      await tprmService.createRiskAcceptance(vendorId, body);
      notifySuccess({
        title: 'Risk Accepted',
        message: 'A new risk acceptance has been recorded.',
      });
      setModalOpen(false);
      load();
    } catch (error) {
      notifyFailure('Failed to Accept Risk', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold">Risk Acceptances</h2>
        <button
          type="button"
          onClick={openCreate}
          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-lg"
        >
          <ShieldCheckIcon className="w-4 h-4" />
          Accept Risk
        </button>
      </div>

      {loading && <div className="text-center py-6 text-gray-500">Loading...</div>}

      {!loading && active && (
        <div className="border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/40 rounded-lg p-4 mb-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <ShieldCheckIcon className="w-5 h-5 text-amber-700 dark:text-amber-300" />
                <span className="text-sm font-semibold text-amber-900 dark:text-amber-100">
                  Active Risk Acceptance
                </span>
              </div>
              <dl className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-1.5 text-sm">
                <dt className="text-gray-500">Reason</dt>
                <dd className="whitespace-pre-wrap">{active.reason}</dd>
                <dt className="text-gray-500">Conditions</dt>
                <dd className="whitespace-pre-wrap">{active.conditions || '—'}</dd>
                <dt className="text-gray-500">Expiry</dt>
                <dd>{formatDate(active.expiry_date)}</dd>
                <dt className="text-gray-500">Accepted At</dt>
                <dd>{formatDateTime(active.accepted_at)}</dd>
                <dt className="text-gray-500">Accepted By</dt>
                <dd>{nameFor(active.accepted_by_oid) || '—'}</dd>
              </dl>
            </div>
            <button
              type="button"
              onClick={() => handleDeactivate(active)}
              className="px-3 py-1.5 text-sm font-medium border border-red-300 text-red-700 dark:text-red-300 dark:border-red-700 hover:bg-red-50 dark:hover:bg-red-950 rounded-lg whitespace-nowrap"
            >
              Deactivate
            </button>
          </div>
        </div>
      )}

      {!loading && !active && (
        <div className="text-center py-6 text-gray-500 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg mb-4">
          No active risk acceptance. Click 'Accept Risk' to record one.
        </div>
      )}

      {!loading && inactive.length > 0 && (
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg">
          <button
            type="button"
            onClick={() => setHistoryOpen((o) => !o)}
            className="w-full flex items-center justify-between px-4 py-2 text-left text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            <span>History ({inactive.length})</span>
            {historyOpen ? (
              <ChevronUpIcon className="w-4 h-4" />
            ) : (
              <ChevronDownIcon className="w-4 h-4" />
            )}
          </button>
          {historyOpen && (
            <div className="border-t border-gray-200 dark:border-gray-700">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr className="text-left">
                    <th className="py-2 px-3">Accepted</th>
                    <th className="py-2 px-3">Reason</th>
                    <th className="py-2 px-3">Expiry</th>
                  </tr>
                </thead>
                <tbody>
                  {inactive.map((ra) => (
                    <tr
                      key={ra.id}
                      className="border-t border-gray-100 dark:border-gray-700"
                    >
                      <td className="py-2 px-3 whitespace-nowrap">
                        {formatDateTime(ra.accepted_at)}
                      </td>
                      <td
                        className="py-2 px-3 text-gray-600 dark:text-gray-300"
                        title={ra.reason}
                      >
                        {excerpt(ra.reason, 120)}
                      </td>
                      <td className="py-2 px-3">{formatDate(ra.expiry_date)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Accept Risk modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-scrim/40">
          <div
            ref={panelRef}
            style={panelStyle}
            className="bg-panel rounded-lg w-full max-w-lg border border-gray-200 dark:border-gray-700"
          >
            <ModalHeader
              title="Accept Risk"
              onClose={saving ? undefined : () => setModalOpen(false)}
              dragHandleProps={dragHandleProps}
            />
            <div className="p-6">
            <div className="flex items-start gap-2 p-3 mb-4 border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/40 rounded-lg">
              <ExclamationTriangleIcon className="w-5 h-5 text-amber-700 dark:text-amber-300 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-900 dark:text-amber-100">
                This will deactivate the current active risk acceptance. A new
                acceptance will be recorded with the reason and conditions you
                provide.
              </p>
            </div>

            <label className="block text-sm font-medium mb-1">
              Reason <span className="text-red-600">*</span>
            </label>
            <textarea
              value={form.reason}
              onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
              rows={3}
              maxLength={5000}
              placeholder="Why is this risk being accepted?"
              className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 mb-3"
            />

            <label className="block text-sm font-medium mb-1">Conditions</label>
            <textarea
              value={form.conditions}
              onChange={(e) => setForm((f) => ({ ...f, conditions: e.target.value }))}
              rows={3}
              maxLength={5000}
              placeholder="Any conditions or compensating controls..."
              className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 mb-3"
            />

            <label className="block text-sm font-medium mb-1">Expiry Date</label>
            <input
              type="date"
              value={form.expiry_date}
              onChange={(e) => setForm((f) => ({ ...f, expiry_date: e.target.value }))}
              className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 mb-4"
            />

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                disabled={saving}
                className="px-3 py-2 text-sm font-medium border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving || !form.reason.trim()}
                className="px-3 py-2 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-lg disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Accept Risk'}
              </button>
            </div>
            </div>
          </div>
        </div>
      )}

      <PanelNotifications
        error={toastError}
        success={toastSuccess}
        onClearError={clearToasts}
        onClearSuccess={clearToasts}
      />
    </div>
  );
}
